import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { observer } from 'mobx-react';
import { QuoteTypeStore } from '../stores/quoteTypeStore';
import { formatCurrency } from '@roc/ui/utils';
import { sum } from '../utils/utils';
import { generateLoanDetailsTableData } from '../steps/summary/utils/summaryUtils';
import { DEFAULT_FEE_CORELOGIX, DEFAULT_FEE_RETAIL_SERVICING_SETUP } from '@roc/feature-loans';

const useStyles = makeStyles(() => ({
  noBorders: {
    border: 'none',
  },
  darkCell: {
    backgroundColor: '#E4E8EB',
  },
  brightCell: {
    backgroundColor: '#FCFCFC',
  },
  boldCell: {
    fontWeight: 'bold',
    color: 'black',
  },
}));

interface FeesDetailsTableProps {
  store: QuoteTypeStore;
}

export const FeesDetailsTableTerm = observer(({ store }: FeesDetailsTableProps) => {
  const classes = useStyles();
  const { pricerSummaryStore, loanDetailsStore } = store
  const { brokerPointsIn } = loanDetailsStore.getFormValues()
  const { submittedPricingOption } = pricerSummaryStore
  const property = store.propertiesStore.getProperties()[0];
  const outputs = store.loanEconomicsStore.outputs;

  function getClassNames(categoryIdx) {
    return `${classes.noBorders} ${categoryIdx % 2 === 0 ? classes.darkCell : classes.brightCell}`;
  }

  const feesDetails = generateLoanDetailsTableData(outputs, 0, property?.loanPurpose, store.loanSubtype);

  const lastRowBackgroundClass =
    feesDetails.categories.length % 2 === 0
      ? classes.darkCell
      : classes.brightCell;

  const lenderFees = [
    { label: 'Origination Fee', value: submittedPricingOption.lenderFeesFormValues.totalOriginationPoints * 0 ?? 0 },
    { label: 'Processing Fee', value: submittedPricingOption.lenderFeesFormValues.totalProcessingFees ?? 0 },
    { label: 'Admin Fee', value: submittedPricingOption.lenderFeesFormValues.totalAdminFees ?? 0 },
    { label: 'Underwriting Fee', value: submittedPricingOption.lenderFeesFormValues.totalUnderwritingFees ?? 0 },
    { label: 'Commitment Fee', value: submittedPricingOption.lenderFeesFormValues.totalCommitmentFees ?? 0 },
    { label: 'Buydown Fee', value: submittedPricingOption.lenderFeesFormValues.totalBuydownFees ?? 0 },
  ].filter(fee => fee.value != null && fee.value > 0);

  const lenderFeesTotal = lenderFees.reduce((sum, fee) => sum + parseFloat(fee.value), 0);

  const thirdPartyFees = [
    { label: 'Broker Fee', value: brokerPointsIn },
    { label: 'Legal Review Fee', value: submittedPricingOption.closingLegalReviewFee },
    { label: 'Corelogix Fee', value: DEFAULT_FEE_CORELOGIX },
    { label: 'Servicing Set Up Fee', value: DEFAULT_FEE_RETAIL_SERVICING_SETUP },
  ].filter(fee => fee.value != null && fee.value > 0);

  const thirdPartyFeesTotal = thirdPartyFees.reduce((sum, fee) => sum + parseFloat(fee.value), 0);

  const grandTotal = lenderFeesTotal + thirdPartyFeesTotal;

  return (
    <TableContainer style={{ border: '1px solid #ddd' }}>
      <Table>
        <TableBody>
          {lenderFees.map((fee, index, filteredFees) => (
            <TableRow key={fee.label}>
              <TableCell className={getClassNames(0)}>
                {index === 0 ? 'Lender Fees' : ''}
              </TableCell>
              <TableCell className={`${getClassNames(0)} ${classes.boldCell}`}>
                {fee.label}
              </TableCell>
              <TableCell className={getClassNames(0)} align="right">
                {formatCurrency(fee.value)}
              </TableCell>
              <TableCell className={`${getClassNames(0)} ${classes.boldCell}`} align="right">
                {index === filteredFees.length - 1 && (
                  formatCurrency(filteredFees.reduce((sum, f) => sum + f.value, 0))
                )}
              </TableCell>
            </TableRow>
          ))}

          {/* Estimated Third Party Fees Section */}
          {thirdPartyFees.map((fee, index, thirdPartyFees) => (
            <TableRow key={fee.label}>
              <TableCell className={getClassNames(1)}>
                {index === 0 ? 'Estimated Third Party Fees' : ''}
              </TableCell>
              <TableCell className={`${getClassNames(1)} ${classes.boldCell}`}>
                {fee.label}
              </TableCell>
              <TableCell className={getClassNames(1)} align="right">
                {formatCurrency(fee.value)}
              </TableCell>
              <TableCell className={`${getClassNames(1)} ${classes.boldCell}`} align="right">
                {index === thirdPartyFees.length - 1 && (
                  formatCurrency(thirdPartyFees.reduce((sum, f) => sum + parseFloat(f.value), 0))
                )}
              </TableCell>
            </TableRow>
          ))}

          {/* Total Row */}
          <TableRow>
            <TableCell className={lastRowBackgroundClass}></TableCell>
            <TableCell className={lastRowBackgroundClass}></TableCell>
            <TableCell
              className={`${lastRowBackgroundClass} ${classes.boldCell}`}
              align="right"
            >
              TOTAL:
            </TableCell>
            <TableCell
              className={`${lastRowBackgroundClass} ${classes.boldCell}`}
              align="right"
            >
              {formatCurrency(grandTotal)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
});
